import { t } from 'i18next';
import type { IObservableArray } from 'mobx';

import type { Goal, Recommendation, TTDCampaign } from '@feathr/blackbox';
import { ERecommendationStatus, ERecommendationType } from '@feathr/blackbox';
import { toast } from '@feathr/components';

import { isEffectiveDateEndReached } from './extendCap';

type TRecommendationContext = 'report' | 'recommendation';

interface IOnChangeRecommendationStatusProps {
  /** The recommendation model */
  recommendation: Recommendation;
  /** Used to determine if the recommendation was applied (recommendation) or auto_dismissed (campaign) */
  context?: TRecommendationContext;
  /** Refreshes the recommendations collection to see if any recommendation statuses have changed */
  onRefreshCollection: () => Promise<void>;
  /** Overrides the generic applied toast for types that have their own copy. */
  appliedMessage?: string;
}

interface IRecommendationPostSaveProps extends IOnChangeRecommendationStatusProps {
  /** The type of recommendation */
  type: ERecommendationType;
  /** The Tradedesk campaign */
  campaign: TTDCampaign;
  /** The user's timezone from account settings */
  timezone: string;
  /** The goals for the campaign */
  goals: IObservableArray<Goal>;
}

/**
 * Returns true if the new target_value (budget) is greater than the current target_value
 * @param campaign - The Tradedesk campaign model
 * @param recommendation - The recommendation model
 * @returns boolean
 */
function getBudgetStatus(campaign: TTDCampaign, recommendation: Recommendation): boolean {
  const newTargetValue = campaign.get('exposure_settings')?.target_value ?? 0;
  const currentTargetValue =
    recommendation.get('current_campaign_values')?.exposure_settings?.target_value ?? 0;
  return newTargetValue > currentTargetValue;
}

/**
 * Whether the underbudget recommendation has actually been acted on.
 *
 * Unlike the Budget recommendation, this one can be resolved two ways — a bigger
 * budget or a re-settled channel split — and backend records a different baseline
 * for each case. Where no baseline exists the answer is "not yet": leaving the card
 * up is safer than clearing it off a campaign that's still underbudget.
 */
function getMetaBudgetStatus(campaign: TTDCampaign, recommendation: Recommendation): boolean {
  const exposureSettings = campaign.get('exposure_settings');
  const budget = exposureSettings?.target_value ?? 0;
  const currentValues = recommendation.get('current_campaign_values')?.exposure_settings;
  const proposedValues = recommendation.get('proposed_campaign_values')?.exposure_settings;

  // Backend proposed a budget (single-channel): applied once the campaign reaches it.
  if (proposedValues?.target_value) {
    return budget >= proposedValues.target_value;
  }
  // Backend captured a budget baseline: applied once the campaign exceeds it.
  if (currentValues?.target_value) {
    return budget > currentValues.target_value;
  }

  /*
   * Dual-channel: backend records only the split baseline today, so a changed
   * split is the only signal available. Feathr/anhinga#7572 still owes a proposed
   * budget and allocations here — until it lands, raising the budget alone leaves
   * the recommendation active.
   */
  const before = currentValues?.channel_split?.allocations ?? [];
  const after = exposureSettings?.channel_split?.allocations ?? [];
  if (!before.length) {
    return false;
  }
  return before.some((allocation) => {
    const updated = after.find((current) => current.channel === allocation.channel);
    return !updated || updated.percentage !== allocation.percentage;
  });
}

/**
 * Returns true if the new date_end has reached (or exceeded) the effective
 * (budget-capped) recommendation. Comparing against the effective date — not
 * the raw proposal — ensures a capped save still clears the recommendation.
 */
function getDateStatus(campaign: TTDCampaign, recommendation: Recommendation): boolean {
  return isEffectiveDateEndReached(campaign, recommendation);
}

/**
 * Updates the status of a recommendation (applied or auto_dismissed) based on the context of the recommendation
 * @param recommendation - The recommendation model
 * @param context - The context of the recommendation (report or recommendation)
 * @param onRefreshCollection - Function to refresh the recommendations collection
 * @returns a function that updates the status of a recommendation
 */
async function handleChangeRecommendationStatus({
  recommendation,
  context = 'recommendation',
  onRefreshCollection,
  appliedMessage,
}: IOnChangeRecommendationStatusProps): Promise<void> {
  const isRecommendationContext = context === 'recommendation';
  const status = ERecommendationStatus.Applied;

  recommendation.set({ status });

  try {
    await recommendation.patchDirty();

    if (isRecommendationContext) {
      toast(
        appliedMessage ??
          t('Applied recommendation: {{title}}', { title: recommendation.get('title') }),
        {
          type: 'info',
        },
      );
    } else {
      toast(
        t('Dismissed recommendation after changes were made in the wizard: {{title}}', {
          title: recommendation.get('title'),
        }),
        {
          type: 'info',
        },
      );
    }

    await onRefreshCollection();
  } catch (e) {
    toast(
      t('An error occurred while updating the status of a recommendation: {{title}}', {
        title: recommendation.get('title'),
      }),
    );
  }
}

/**
 * Updates the status of a recommendation based on the type of recommendation.
 * This will be done by the backend in the future.
 * @param type - The type of recommendation
 * @param campaign - The Tradedesk campaign model
 * @param recommendation - The recommendation model
 * @param onRefreshCollection - Function to refresh the recommendations collection
 * @param context - The context of the recommendation (campaign or recommendation)
 * @param goals - The goals for the campaign
 * @returns a function that updates the status of a recommendation
 */
export function updateRecommendationStatus({
  type,
  campaign,
  recommendation,
  onRefreshCollection,
  context,
  goals,
}: IRecommendationPostSaveProps): () => Promise<void> {
  const handleApplyRecommendation = async (appliedMessage?: string): Promise<void> => {
    await handleChangeRecommendationStatus({
      recommendation,
      onRefreshCollection,
      context,
      appliedMessage,
    });
  };

  const recommendationMap = {
    [ERecommendationType.Budget]: async (): Promise<void> => {
      const shouldChangeStatus = getBudgetStatus(campaign, recommendation);

      // Don't apply the recommendation unless the new target value is greater than the current target value.
      // This allows the user to partially apply the recommendation without going under the original.
      if (shouldChangeStatus) {
        handleApplyRecommendation();
      }
    },
    [ERecommendationType.ExtendCampaign]: async (): Promise<void> => {
      const shouldChangeStatus = getDateStatus(campaign, recommendation);

      // Don't apply the recommendation unless the new date_end is the same or after the recommended date_end.
      if (shouldChangeStatus) {
        handleApplyRecommendation();
      }
    },
    [ERecommendationType.BudgetDuration]: async (): Promise<void> => {
      const hasLargerBudget = getBudgetStatus(campaign, recommendation);
      const hasRecommendedDateEndOrLater = getDateStatus(campaign, recommendation);

      // Don't apply the recommendation unless the new target value is greater than the current target value
      // and the new date_end is the same or after the recommended date_end.
      // This allows the user to partially apply the recommendation without going under the original.
      if (hasLargerBudget && hasRecommendedDateEndOrLater) {
        handleApplyRecommendation();
      }
    },
    [ERecommendationType.OmniCampaignInsufficientBudget]: async (): Promise<void> => {
      // Only clear the card once the budget or the split actually resolves the shortfall.
      if (getMetaBudgetStatus(campaign, recommendation)) {
        handleApplyRecommendation(
          t('Updated budget for "{{name}}".', {
            name: campaign.get('name'),
          }),
        );
      }
    },
    [ERecommendationType.MissingGoal]: async (): Promise<void> => {
      const hasSavedGoal = goals?.some((goal) => !goal.isEphemeral);

      // If the user saved a goal, apply the recommendation
      if (hasSavedGoal) {
        handleApplyRecommendation();
      }
    },
    [ERecommendationType.EnableOmnitargeting]: async (): Promise<void> => {
      // TODO: Implement
      return;
    },
    [ERecommendationType.IncreaseBaseBid]: async (): Promise<void> => {
      // TODO: Implement
      return;
    },
    [ERecommendationType.IncreaseFrequencyCap]: async (): Promise<void> => {
      // TODO: Implement
      return;
    },
    [ERecommendationType.UpdateCreatives]: async (): Promise<void> => {
      // TODO: Implement
      return;
    },
  };

  /* `type` is whatever the backend sent, not necessarily a member of the enum. An
   * unmapped one threw from `await onPostSave()` after the campaign had already saved. */
  return recommendationMap[type] ?? ((): Promise<void> => Promise.resolve());
}
